import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import Button, {
  ButtonIconPosition,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import FormInput, {
  InputType,
} from '@/components/BaseLibrary/FormInput/FormInput';
import { IconName } from '@/components/Icon/Icon';
import useEcommMarket, { getCloudfrontUserLocation } from '@/hooks/geo';
import usStates from '@/utils/usStates';
import { sleep } from '@/utils/utils';
import { InputName, zipCodeValidator } from '@/utils/validation/inputs';
import useStyles from './Location.styles';

export interface LocationProps {
  closeLocation: () => void;
  isOnDark: boolean;
}

const locationSchema = z.object({
  ...zipCodeValidator,
});

type LocationData = z.infer<typeof locationSchema>;

/** US ZIP+4, spaces, or missing leading zeros break `zipcodes.lookup` unless normalized. */
function normalizeZipForLookup(zipCode: string): string {
  const digits = zipCode.replace(/\D/g, '').slice(0, 5);
  if (digits.length === 0) {
    return zipCode.trim();
  }
  return digits.padStart(5, '0');
}

const Location = ({ closeLocation, isOnDark }: LocationProps) => {
  const { classes, cx } = useStyles({ isOnDark });
  const { userLocation, setUserLocation } = useEcommMarket();

  const locationClass = cx(classes.location, isOnDark && classes.locationDark);

  const [isUpdatingLocation, setIsUpdatingLocation] = useState<boolean>(false);

  const zipInputRef = useRef<HTMLInputElement | null>(null);

  const {
    trigger,
    register,
    formState: { errors, isSubmitSuccessful, isValid },
    handleSubmit,
  } = useForm<LocationData>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      [InputName.ZIP_CODE]: '',
    },
  });

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      zipInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const isDisabled =
    Object.keys(errors).length > 0 ||
    isSubmitSuccessful ||
    isUpdatingLocation ||
    !isValid;

  // Scroll to top of screen while updating location
  // In useEffect because it interferes with react state change re-render
  useEffect(() => {
    if (isUpdatingLocation) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isUpdatingLocation]);

  // Updates the user's state from either a submitted zip code or their current location then closes the drawer with animation
  async function updateLocation(zipCode?: string) {
    const zipcodes = (await import('zipcodes')).default;
    setIsUpdatingLocation(true);
    if (zipCode) {
      const normalizedZip = normalizeZipForLookup(zipCode);
      const location = zipcodes.lookup(normalizedZip);
      const state = location?.state;
      const city = location?.city ?? null;
      const resolvedZip = location?.zip ?? normalizedZip;

      if (state && state in usStates) {
        setUserLocation({
          state,
          zipCode: resolvedZip,
          country: 'US',
          city,
        });
      }
    } else {
      setUserLocation(getCloudfrontUserLocation());
    }

    await sleep(1000);
    closeLocation();
    setIsUpdatingLocation(false);
  }

  const onSubmit: SubmitHandler<LocationData> = async (data: LocationData) => {
    await updateLocation(data.zipCode);
  };
  return (
    <div className={locationClass}>
      <div className={classes.locationInner}>
        <h2 className={classes.locationTitle}>
          We use your location to provide localized solar offers and incentives.
        </h2>
        <div className={classes.inputWrapper}>
          <FormInput
            label='Zip code'
            placeholder='Enter your Zip code'
            testId='zip-input'
            type={InputType.ZIP}
            inputMode='numeric'
            isOnDark={isOnDark}
            error={errors[InputName.ZIP_CODE]?.message}
            name={InputName.ZIP_CODE}
            register={register}
            inputRef={zipInputRef}
            onBlur={(event) => {
              if (event.target.value !== '') {
                trigger();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit(onSubmit)(event);
              }
            }}
          />
        </div>
        <div className={classes.buttonContainer}>
          <Button
            label='Update'
            isOnDark={isOnDark}
            variant={ButtonVariant.PRIMARY}
            disabled={isDisabled}
            onClick={handleSubmit(onSubmit)}
          />
          {userLocation?.state && userLocation.state in usStates && (
            <>
              <div className={classes.lineContainer}>
                <div className={classes.line} />
                <span>or</span>
                <div className={classes.line} />
              </div>
              <Button
                label='Use my current location'
                variant={ButtonVariant.SECONDARY}
                isOnDark={isOnDark}
                iconName={IconName.PIN}
                iconPosition={ButtonIconPosition.LEFT}
                onClick={() => updateLocation()}
                disabled={isUpdatingLocation}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Location;
